<script lang="ts" setup>
import { useRedirect } from "@core/composables"
import { useSourceStore } from "@core/stores/source.store"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatIdSource } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    /** Identifiant de la source (_id_source). */
    sourceId: string
    /** Titre de la page. */
    title: string
    /** Icône PrimeIcons (ex: "pi pi-home"). */
    icon?: string
    /** Affiche un skeleton à la place du breadcrumb. */
    loading?: boolean
    /**
     * Redirection fil d’Ariane (router).
     * Fourni par HeaderPageSource ; défaut : useRedirect interne.
     */
    onNavigate?: (url: string) => void
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    icon: "",
    loading: false,
    onNavigate: undefined,
})

// ================================
// COMPOSABLES
// ================================

const sourceStore = useSourceStore()
const { redirectWrapper } = useRedirect()

const { t } = useI18n({
    messages: {
        fr: {
            home: "Accueil",
        },
    },
})

// ================================
// ÉTAT
// ================================

// ================================
// DONNÉES CALCULÉES
// ================================

const source = computed(() =>
    props.sourceId ? sourceStore.getSourceById(props.sourceId) : undefined
)

/**
 * Génère la structure du breadcrumb : Accueil → Source (idsource).
 */
const getBreadCrumb = computed(() => {
    const breadCrumb: { icon: string; label: string; route?: string }[] = [
        { icon: "pi pi-home", label: t("home"), route: "/" },
    ]
    if (source.value) {
        const label =
            source.value.name ||
            formatIdSource(
                source.value.idsource ?? "",
                source.value.idtype ?? source.value.type ?? ""
            )
        breadCrumb.push({
            icon: "icon-source",
            label: label || source.value.name || props.sourceId,
        })
    }
    breadCrumb.push({
        label: props.title,
        icon: props.icon,
    })
    return breadCrumb
})

// ================================
// INTERACTIONS
// ================================

/**
 * Quand on clique sur un élément du breadcrumb (accueil = `/`).
 * @param route - La route où naviguer
 */
function onClickNavigate(route: string): void {
    const navigate = props.onNavigate ?? redirectWrapper
    navigate(route)
}
</script>
<template>
    <!-- ================================ -->
    <!-- LOADING STATE -->
    <!-- ================================ -->
    <div
        v-if="loading"
        class="breadcrumb-source-loading flex items-center gap-2"
    >
        <BaseSkeleton width="20rem" height="1.5rem" />
    </div>

    <!-- ================================ -->
    <!-- BREADCRUMB -->
    <!-- ================================ -->
    <BaseBreadCrumb
        v-else-if="getBreadCrumb.length >= 2"
        :model="getBreadCrumb"
        :home="null"
        class="breadcrumb-source text-blue-400"
    >
        <template #item="{ item, props: propsItem }">
            <div class="flex items-center gap-2">
                <i
                    class="p-breadcrumb-item-icon"
                    v-if="item.icon && item.icon !== 'icon-source'"
                    :class="item.icon"
                />
                <BaseSourceIcon
                    v-else-if="item.icon === 'icon-source'"
                    :type="source?.type ?? source?.idtype ?? ''"
                    size="16"
                />
                <button
                    v-if="item.route"
                    type="button"
                    v-bind="propsItem.action"
                    @click.prevent="onClickNavigate(item.route)"
                    class="cursor-pointer"
                >
                    <span class="hover:underline">{{ item.label }}</span>
                </button>
                <p
                    v-else
                    :href="item.url"
                    :target="item.target"
                    v-bind="propsItem.action"
                >
                    <span class="">{{ item.label }}</span>
                </p>
            </div>
        </template>
    </BaseBreadCrumb>
</template>
